import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import { useNotify } from "../../context/NotificationContext";
import { deleteLesson } from "../../services/lessonService/lessonService";
import {
  fetchKnowledgeBaseCourse,
  fetchKnowledgeBaseLesson,
} from "../../services/knowledgeBaseService/knowledgeBaseService";
import type {
  KnowledgeBaseCourse,
  KnowledgeBaseLesson,
} from "../../services/knowledgeBaseService/knowledgeBaseService.types";
import { logger } from "../../utils/logger";
import StudentMediaList from "../StudentPortal/StudentMediaList";
import StudentVideoSplitView, { type StudentVideoSelection } from "../StudentPortal/StudentVideoSplitView";
import KnowledgeBaseLessonEditModal from "./KnowledgeBaseLessonEditModal";
import {
  ContentWrapper,
  CoursesPageContainer,
  Title,
} from "../CoursesPage/CoursesPage.styles";
import {
  DetailMeta,
  EmptyState,
  KnowledgeBaseCourseEditAction,
  KnowledgeBaseCourseHeader,
  KnowledgeBaseCourseHeaderMain,
  MaterialLink,
  RichContent,
  SectionTitle,
} from "./KnowledgeBasePage.styles";

const KnowledgeBaseLessonPage: React.FC = () => {
  const { courseId, lessonId } = useParams();
  const courseIdNumber = Number(courseId);
  const lessonIdNumber = Number(lessonId);
  const [course, setCourse] = useState<KnowledgeBaseCourse | null>(null);
  const [lesson, setLesson] = useState<KnowledgeBaseLesson | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<StudentVideoSelection | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const toast = useNotify();
  const navigate = useNavigate();

  const handleDeleteLesson = async () => {
    if (!course || !lesson) return;

    try {
      await deleteLesson(course.id, lesson.id);
      setDeleteModalOpen(false);
      toast.show({
        severity: "success",
        summary: "Удалено",
        detail: "Урок удалён",
        life: 2500,
      });
      navigate("/knowledge-base/" + course.id);
    } catch (requestError) {
      logger.error("Ошибка удаления урока Базы знаний:", requestError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось удалить урок",
        life: 3000,
      });
    }
  };

  useEffect(() => {
    setSelectedVideo(null);
  }, [lessonIdNumber]);

  useEffect(() => {
    if (!courseIdNumber || !lessonIdNumber) return;
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetchKnowledgeBaseCourse(courseIdNumber),
      fetchKnowledgeBaseLesson(courseIdNumber, lessonIdNumber),
    ])
      .then(([courseData, lessonData]) => {
        if (!active) return;
        setCourse(courseData);
        setLesson(lessonData);
        window.dispatchEvent(new Event("knowledge-base-status-changed"));
      })
      .catch((requestError) => {
        logger.error("Ошибка загрузки материала Базы знаний:", requestError);
        if (!active) return;
        setError("Материал недоступен. Проверьте активный тариф или вернитесь в каталог.");
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось открыть материал Базы знаний",
          life: 3000,
        });
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [courseIdNumber, lessonIdNumber, toast]);

  return (
    <CoursesPageContainer>
      <Sidebar />
      <ContentWrapper>
        <Breadcrumbs
          items={[
            { label: "База знаний", to: "/knowledge-base" },
            ...(course
              ? [{ label: course.title, to: `/knowledge-base/${course.id}` }]
              : []),
            { label: lesson?.title || "Материал" },
          ]}
        />

        {loading ? (
          <EmptyState>Загружаем материал…</EmptyState>
        ) : error || !lesson || !course ? (
          <EmptyState>{error || "Материал не найден."}</EmptyState>
        ) : (
          <StudentVideoSplitView video={selectedVideo} onClose={() => setSelectedVideo(null)}>
            <KnowledgeBaseCourseHeader>
              <KnowledgeBaseCourseHeaderMain>
                <Title>{lesson.title}</Title>
                <DetailMeta>
                  {course.title} · материал {lesson.order}{lesson.category_title ? ` · ${lesson.category_title}` : ""}
                </DetailMeta>
              </KnowledgeBaseCourseHeaderMain>

              {course.can_manage ? (
                <KnowledgeBaseCourseEditAction>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setEditModalOpen(true)}
                  >
                    Редактировать урок
                  </Button>
                  <Button
                    type="button"
                    variant="dangerSecondary"
                    size="md"
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    Удалить урок
                  </Button>
                </KnowledgeBaseCourseEditAction>
              ) : null}
            </KnowledgeBaseCourseHeader>

            <SectionTitle>Содержание</SectionTitle>
            {lesson.content ? (
              <RichContent dangerouslySetInnerHTML={{ __html: lesson.content }} />
            ) : (
              <EmptyState>Текстового материала нет.</EmptyState>
            )}

            <SectionTitle>Файлы и видео</SectionTitle>
            {lesson.video_url ? (
              <MaterialLink href={lesson.video_url} target="_blank" rel="noreferrer">
                Открыть дополнительный материал
              </MaterialLink>
            ) : null}
            <StudentMediaList
              items={lesson.files_from_storage}
              lessonId={lesson.id}
              variant="lesson"
              selectedVideoId={selectedVideo?.id}
              onVideoSelect={(video) =>
                setSelectedVideo({
                  ...video,
                  contextLabel: "Видео из Базы знаний. Текст материала остаётся доступным слева во время просмотра.",
                })
              }
            />
            {!lesson.video_url && lesson.files_from_storage.length === 0 ? (
              <EmptyState>К этому материалу файлы и видео не добавлены.</EmptyState>
            ) : null}
          </StudentVideoSplitView>
        )}

        <ConfirmModal
          isOpen={deleteModalOpen && Boolean(course?.can_manage)}
          title="Удаление урока"
          message={"Вы уверены, что хотите удалить урок «" + (lesson?.title || "") + "»?"}
          confirmButtonText="Удалить"
          confirmVariant="danger"
          onConfirm={() => void handleDeleteLesson()}
          onCancel={() => setDeleteModalOpen(false)}
        />

        <KnowledgeBaseLessonEditModal
          open={editModalOpen && Boolean(course?.can_manage)}
          courseId={courseIdNumber}
          lessonId={lessonIdNumber || null}
          onClose={() => setEditModalOpen(false)}
          onSaved={async () => {
            const lessonData = await fetchKnowledgeBaseLesson(
              courseIdNumber,
              lessonIdNumber,
            );
            setLesson(lessonData);
          }}
        />
      </ContentWrapper>
    </CoursesPageContainer>
  );
};

export default KnowledgeBaseLessonPage;
